import { z } from 'zod';
import { labelArray, longText, name, uuid } from './fields';

/**
 * Master List Items — templated items inside a Master List (data-model.md §4,
 * api-contract §3). Nested under their parent list; the parent list owner
 * governs access. `position` is server-managed (append on create, contiguous on
 * delete, set only via the batch reorder endpoint).
 */

/** Full Master List Item resource (response shape — api-contract §3). */
export const MasterListItem = z.object({
  id: uuid(),
  masterListId: uuid(),
  name: name(),
  description: longText(),
  defaultLabels: z.array(z.string()),
  isOptional: z.boolean(),
  position: z.number().int(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type MasterListItem = z.infer<typeof MasterListItem>;

/**
 * POST body (api-contract §3.2). Server sets `masterListId` from the path,
 * assigns `position` = max+1, timestamps. `defaultLabels` defaults to `[]`,
 * `isOptional` to `false`. `position` is NOT client-settable here.
 */
export const CreateMasterListItemInput = z.object({
  name: name(),
  description: longText().optional(),
  defaultLabels: labelArray().optional().default([]),
  isOptional: z.boolean().optional().default(false),
});
export type CreateMasterListItemInput = z.infer<typeof CreateMasterListItemInput>;

/** POST /items/bulk body — append multiple normal master-list items at once. */
export const CreateMasterListItemsBulkInput = z.object({
  items: z.array(CreateMasterListItemInput).min(1).max(50),
});
export type CreateMasterListItemsBulkInput = z.infer<typeof CreateMasterListItemsBulkInput>;

/** User-facing OpenRouter model choices for library-list item suggestions. */
export const AiSuggestionModel = z.enum([
  'gpt_best',
  'gpt_fast',
  'opus_best',
  'claude_fast',
  'gemini_best',
  'gemini_fast',
]);
export type AiSuggestionModel = z.infer<typeof AiSuggestionModel>;

export const SuggestMasterListItemsInput = z.object({
  model: AiSuggestionModel.default('gpt_fast'),
});
export type SuggestMasterListItemsInput = z.infer<typeof SuggestMasterListItemsInput>;

export const SuggestedMasterListItem = z.object({
  name: name(),
  description: longText().optional(),
});
export type SuggestedMasterListItem = z.infer<typeof SuggestedMasterListItem>;

export const SuggestMasterListItemsOutput = z.object({
  suggestions: z.array(SuggestedMasterListItem).max(12),
});
export type SuggestMasterListItemsOutput = z.infer<typeof SuggestMasterListItemsOutput>;

/**
 * POST /items/parse body — a freeform text dump and/or a photo, plus the model.
 * The AI extracts checklist items *purely from the supplied input* (a pasted
 * note/email/recipe, or a snapshot of a written list), so they become selectable
 * items. At least one of `text` / `image` must be present. `image` is a base64
 * data URL (the client resizes before sending).
 */
export const ParseMasterListItemsInput = z
  .object({
    model: AiSuggestionModel.default('gpt_fast'),
    text: z.string().trim().max(20_000).optional(),
    image: z
      .string()
      .regex(/^data:image\/(png|jpeg|webp);base64,/, 'Image must be a PNG, JPEG, or WebP data URL')
      .max(12_000_000)
      .optional(),
  })
  .refine((input) => Boolean(input.text?.length) || Boolean(input.image), {
    message: 'Provide text or a photo',
  });
export type ParseMasterListItemsInput = z.infer<typeof ParseMasterListItemsInput>;

export const ParseMasterListItemsOutput = z.object({
  suggestions: z.array(SuggestedMasterListItem).max(50),
});
export type ParseMasterListItemsOutput = z.infer<typeof ParseMasterListItemsOutput>;

/**
 * PATCH body (api-contract §3.3) — all optional. `position` is intentionally
 * absent (reorder goes through §3.5); an incoming `position` key is stripped.
 */
export const UpdateMasterListItemInput = z
  .object({
    name: name(),
    description: longText(),
    defaultLabels: labelArray(),
    isOptional: z.boolean(),
  })
  .partial();
export type UpdateMasterListItemInput = z.infer<typeof UpdateMasterListItemInput>;

/**
 * PATCH /items/reorder body (api-contract §3.5). `itemIds` MUST be the complete
 * set of the list's current item ids, each exactly once, in desired order. The
 * server assigns `position = index`. Non-empty, all UUIDs, no duplicates — the
 * exact-set match against the list is enforced in the handler (T07).
 */
export const ReorderMasterListItemsInput = z.object({
  itemIds: z
    .array(uuid())
    .min(1)
    .refine((ids) => new Set(ids).size === ids.length, {
      message: 'itemIds must not contain duplicates',
    }),
});
export type ReorderMasterListItemsInput = z.infer<typeof ReorderMasterListItemsInput>;
